import {useCallback, useContext, useMemo} from "react";
import {GlobalContext} from "@tools/globalContext";
import { EPageType } from "@extension/config/pagesConfig";
import {ESecurityGroupRights} from "@interface/enums";
import {pageToIndexMapping} from "@extension/config/userRightsPageToIndexMapping"

interface IMenuVisibility {
    showQRCode?: boolean
    showLocation?: boolean
    canRemove?: boolean
    canRead?: boolean
    canEdit?: boolean
    canCreate?: boolean
}

const useUserRights = () => {
    const { user, pagesConfig, pagesDecorator } = useContext(GlobalContext);

    const defaultPage = useMemo(() => {
        const findDefault = ((pages: any[]) => {
            if(!(pages instanceof Array))
                return ""

            for(let page of (pages || [])){

                if (page["default"] && (page["default"].includes(user?.role?.id) || page["default"].includes("all"))) {
                    return page.id
                }
                else if(page.children){
                    const defPage: string = findDefault(page.children)
                    if (defPage)
                        return defPage
                }
            }
            return ""
        })

        return findDefault(pagesDecorator)
    }, [pagesDecorator, user])


    const findPath = useCallback((pageName: string, id?:string) => {
        const resultPath: string[] = []
        const checkBranch = (arr: any[]) => {
            if(!(arr instanceof Array))
                return false
            
            const allowPages = (arr || []).filter(page =>(!page.role || page.role.includes(user?.role?.id)) && (!page.type || page.type.includes(user?.type)))
            for(let page of allowPages){
                const pageConfig = pagesConfig.pages.find(it => it.id === page.id)
                let urlPart = pageConfig?.url || page.id
                if(pageConfig?.type === EPageType.params || pageConfig?.type === EPageType.tabs) {
                    urlPart = id ? page.id : pageConfig?.paramName ? `:${pageConfig?.paramName}` : page.id
                }
                else if(pageConfig?.type === EPageType.item ) {
                    urlPart =  ":id"
                }

                if(page.id === pageName) {
                    resultPath.push(urlPart);
                    return true
                }
                if(page.children) {
                    resultPath.push(urlPart);
                    const found = checkBranch(page.children)
                    if (!found)
                        resultPath.pop()
                    else
                        return true
                }
            }
            return false
        }

        checkBranch(pagesDecorator)
        if(!resultPath.length)
            return ""

        return `${pagesConfig.rootUrl ? `/${pagesConfig.rootUrl}` : ""}/${resultPath.join("/")}`

    }, [pagesConfig, pagesDecorator])

    const getCurrentRights = useCallback((id: string) => {
        if (!user.role?.rights)
            return {permissions : ["all"]}

        let currentRights = user.role?.rights?.find((item: any)  => item.entity === id)

        if (!currentRights)
            currentRights = {permissions : [user.role?.defaultRights]}

        return currentRights
    }, [user])


    const menuItemVisibility = useCallback((gridId: string, item? : any) => {
        let defaultRights : IMenuVisibility = {
            showQRCode: true,
            showLocation: true,
            canRead: true,
            canEdit: true,
            canCreate: true,
            canRemove: true
        }

        let userRights : IMenuVisibility = {}

        let currentRights = getCurrentRights(gridId)

        if (currentRights?.permissions?.[0] === "denied") {
            userRights.canRead = false
            userRights.canEdit = false
            userRights.canRemove = false
            userRights.canCreate = false
        }
        if (currentRights?.permissions?.some((rule : string )=> rule === ESecurityGroupRights.read)) {
            userRights.canEdit = false
            userRights.canRemove = false
            userRights.canCreate = false
        }
        if (currentRights?.permissions?.some((rule : string )=> rule === ESecurityGroupRights.update)) {
            userRights.canEdit = true
        }
        if (currentRights?.permissions?.some((rule : string )=> rule === ESecurityGroupRights.remove)) {
            userRights.canRemove = true
        }
        if (currentRights?.permissions?.some((rule : string )=> rule === ESecurityGroupRights.create)) {
            userRights.canCreate = true
        }

        if (item?.denyUpdate)
            userRights.canEdit = false

        if (item?.denyRemove)
            userRights.canRemove = false

        return Object.assign(defaultRights, userRights)
    }, [getCurrentRights])

    const navigatorVisibility = useCallback((page : any) => {
        const indexId = pageToIndexMapping[page.id]
        if (!indexId) {
            if ((!page.role || page.role.includes(user?.role?.id)) && (!page.type || page.type.includes(user?.type))){
                return true
            }
            return false
        }
        else {
            if(page.type?.length && !page.type?.includes(user?.type))
                return false
        }

        let currentRights = getCurrentRights(indexId)

        if (currentRights?.permissions?.[0] === "denied") {
            return false
        }
        else if (currentRights?.permissions?.[0] === "all") {
            if (!page.role || page.role.includes(user?.role?.id))
                return true
        }
        if (currentRights?.permissions?.some((rule : string )=> rule === "read")) {
            if (!page.role || page.role.includes(user?.role?.id))
                return true
        }

        return false
    }, [getCurrentRights, user])

    const firstPageUrl = useMemo(() => {
        if (navigatorVisibility({id: defaultPage})) {
            return findPath(defaultPage) + "/"
        }

        const findFirstAllowPage = (pages: any[]): string => {
            const allowByDecoratorPages = pages.filter(page => (!page.role || page.role.includes(user?.role?.id)) && (!page.type || page.type.includes(user?.type)))
            for(const page of allowByDecoratorPages) {
                if (navigatorVisibility(page)) {
                    return findPath(page.id)
                }
                else if(page.children) {
                    return findFirstAllowPage(page.children)

                }
            }
            return ""
        }
        return findFirstAllowPage(pagesDecorator) + "/"

    }, [navigatorVisibility, defaultPage])

    const gridPluginsVisibility = useCallback((gridId: string, pluginRights: any) => {
        if (!pluginRights)
            return true
        let currentRights = getCurrentRights(gridId)

        return currentRights.permissions.includes(pluginRights)
    }, [getCurrentRights])

    return {firstPageUrl, menuItemVisibility, navigatorVisibility, gridPluginsVisibility, findPath, defaultPage}
}

export default useUserRights
